import { useMemo, useState } from 'react'

import { Typography, Button, Card, Breadcrumbs } from '@mui/material'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  Wifi,
  Battery,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  BatteryWarning,
  Download,
  Bell,
  Tag,
  TrendingUp,
  IdCard,
  Settings,
  Activity,
  ArrowLeft,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  SignalBars,
  StatusBadge,
  SectionHeader,
  InfoRow,
  LegendItem,
} from '../../../common/components'
import { formatDateOnly } from '../../../common/functions/timeConversion'
import { ROUTES } from '../../../constants/routes'
import { diffFromNow, formatDateTime, useTimezone } from '../../../utils/datetime'

import { filterDeviceDatalogsApi, getAlarmProfileByUuid, getDeviceByUuid } from './api'
import AssetTagHistoryTable from './AssetTagHistoryTable'

// ── data ───────────────────────────────────────────────────────────────────
// Per-metric display palette. Each chart gets its own stroke colour and
// gradient id so two charts can sit side-by-side without their gradient
// defs colliding (SVG defs are global per document).
// All chart lines render with a single brand green stroke so every
// sensor reads with the same identity. Gradient id kept in the palette
// for backwards compat with `gradientId` references but the chart no
// longer renders an area fill.
const CHART_LINE_COLOR = '#2fbf71'
const SENSOR_PALETTE = {
  Temperature: { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' },
  'Rel. Humidity': { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' },
  Dewpoint: { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' },
}

// Pick a lucide Battery tier icon + colour based on the device's battery
// percentage. Lucide ships four filled variants so the icon itself signals
// the level — `Battery` (outline) is the fallback for missing values.
// Thresholds: green >= 80, amber 40–79, red low < 40.
const getBatteryIcon = (pct) => {
  if (!Number.isFinite(pct)) return { Icon: Battery, color: 'var(--text-3, #6b7280)' }
  if (pct >= 80) return { Icon: BatteryFull, color: '#1fd8a8' }
  if (pct >= 40) return { Icon: BatteryMedium, color: '#1fd8a8' }
  if (pct >= 15) return { Icon: BatteryLow, color: '#ffa65c' }
  return { Icon: BatteryWarning, color: '#ff3d57' }
}

// Per-key label/unit overrides for `device.lastLogData`. Each entry
// surfaces a measurement with a friendly title and matching unit so the
// Current Readings card can render one pill per sensor without leaking
// the raw API key. Unknown keys fall through to a generic label with no
// unit so the layout still works for fields the backend hasn't whitelisted.
const CURRENT_READING_META = {
  temperature: { label: 'Temperature', unit: '°C' },
  humidity: { label: 'Rel. Humidity', unit: '%' },
  dewpoint: { label: 'Dewpoint', unit: '°C' },
  signal: { label: 'Signal', unit: 'dBm' },
}

// Map `device.lastLogData` to `{ key, label, value, unit }` rows. Numeric
// values render as-is; non-numeric values fall back to the raw string.
// Units / labels come from `CURRENT_READING_META` so a future API key
// (e.g. `co2`) renders gracefully with an empty unit.
const buildCurrentReadings = (lastLogData) => {
  if (!lastLogData || typeof lastLogData !== 'object') return []
  return Object.entries(lastLogData).map(([key, value]) => {
    const meta = CURRENT_READING_META[key] || { label: key, unit: '' }
    return { key, label: meta.label, unit: meta.unit, value }
  })
}

// Build the Device Health rows from the API payload. Battery and Signal
// values come from `device.batteryLevel` / `device.signal` (both nullable).
// When either is null/undefined we render `--` so the layout never shows
// a fake percentage from a stale fallback.
const buildHealthRows = (device) => {
  const batteryRaw = device?.batteryLevel
  const signalRaw = device?.signal
  const batteryPct = Number(batteryRaw)
  const signalPct = Number(signalRaw)
  const hasBattery = Number.isFinite(batteryPct) && batteryRaw !== null && batteryRaw !== ''
  const hasSignal = Number.isFinite(signalPct) && signalRaw !== null && signalRaw !== ''
  const { Icon: BatteryIcon, color: batteryColor } = getBatteryIcon(hasBattery ? batteryPct : NaN)
  return [
    {
      label: 'Status',
      icon: <Wifi size={14} />,
      value: device?.status ? (
        <StatusBadge color={device.status === 'ACTIVE' ? 'green' : 'red'} dot>
          {device.status === 'ACTIVE' ? 'Online' : 'Offline'} ·{' '}
          {device.status === 'ACTIVE' ? 'Live' : 'No data'}
        </StatusBadge>
      ) : (
        '--'
      ),
    },
    {
      label: 'Battery',
      icon: <Battery size={14} />,
      value: hasBattery ? (
        <div className="flex items-center gap-1" style={{ color: batteryColor }}>
          <BatteryIcon size={22} color={batteryColor} /> {Math.round(batteryPct)}%
        </div>
      ) : (
        '--'
      ),
    },
    {
      label: 'Signal',
      icon: <Activity size={14} />,
      value: hasSignal ? (
        <SignalBars
          value={signalPct}
          level={Math.min(4, Math.max(0, Math.round((signalPct / 100) * 4)))}
        />
      ) : (
        '--'
      ),
    },
    { label: 'Last data', icon: <Activity size={14} />, value: <span>--</span> },
  ]
}

// ── chart helpers ──────────────────────────────────────────────────────────
const CHART_W = 600
const CHART_H = 160
const GRID_Y = [20, 50, 80, 110, 140]

// Fuzzy lookup by sensor name (case-insensitive substring). Used to map
// the API's `device.sensors[].name` to a palette + sample series when the
// exact name isn't a key in the lookup tables.
const findKey = (name, table) => {
  if (!name) return undefined
  if (table[name]) return name
  const hit = Object.keys(table).find((k) => name.toLowerCase().includes(k.toLowerCase()))
  return hit
}

// Map raw `/device-datalogs/filter` rows to `{ time, v }` points for the
// SVG chart. Sorts ascending by `logDateTime` so the line draws left→right
// and formats the timestamp as HH:mm (24h). Falls back to an empty array
// when the API hasn't returned any rows yet — the chart will simply render
// an empty axis until the query resolves.
const mapDatalogsToSeries = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return []
  return [...rows]
    .sort((a, b) => new Date(a.logDateTime) - new Date(b.logDateTime))
    .map((row) => {
      const d = new Date(row.logDateTime)
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      // `date` is formatted via `formatDateOnly` so the tooltip can
      // surface both the time and the calendar day without leaking
      // timezone math into the chart code. Falls back to the raw
      // ISO slice (YYYY-MM-DD) when `formatDateOnly` returns null/empty
      // for an unparseable input.
      const value = Number(row.sensorValue)
      const dateText = typeof formatDateOnly === 'function' ? formatDateOnly(row.logDateTime) : null
      return {
        time: `${hh}:${mm}`,
        date: dateText || row.logDateTime?.slice(0, 10) || '',
        v: Number.isFinite(value) ? value : null,
      }
    })
    .filter((p) => p.v !== null)
}

// Aggregate `alarmProfile[].profiles[]` into the four chart bands
// (warn upper/lower, crit upper/lower). Each `profile` carries
// `severity` (`WARNING` | `CRITICAL`), `maxValue`, `minValue`, and
// optional `rangeNumber`. When a sensor ships multiple bands of the
// same severity we collapse them to the outer envelope — the highest
// `maxValue` and lowest `minValue` — so the dashed line shows the
// furthest edge of any active band. Returns `null` for any field that
// isn't present in the API payload so the chart can still skip the
// line when the backend hasn't supplied one. `maxValue`/`minValue`
// arrive as strings from the backend, so we coerce via `Number()`.
const bandsFromAlarmProfile = (profiles) => {
  if (!Array.isArray(profiles) || profiles.length === 0) {
    return { warnUpper: null, warnLower: null, critUpper: null, critLower: null }
  }
  const bySeverity = { WARNING: [], CRITICAL: [] }
  for (const p of profiles) {
    const sev = (p?.severity || '').toUpperCase()
    if (!bySeverity[sev]) continue
    const min = Number(p.minValue)
    const max = Number(p.maxValue)
    if (!Number.isFinite(min) || !Number.isFinite(max)) continue
    bySeverity[sev].push({ min, max })
  }
  const reduceBands = (bands) => {
    if (bands.length === 0) return { upper: null, lower: null }
    return {
      upper: Math.max(...bands.map((b) => b.max)),
      lower: Math.min(...bands.map((b) => b.min)),
    }
  }
  const warn = reduceBands(bySeverity.WARNING)
  const crit = reduceBands(bySeverity.CRITICAL)
  return {
    warnUpper: warn.upper,
    warnLower: warn.lower,
    critUpper: crit.upper,
    critLower: crit.lower,
  }
}

// No local threshold fallback — bands are driven exclusively by the
// `alarmProfile` payload from `/device-datalogs/filter`. When the API
// hasn't returned a profile for a sensor (or hasn't returned one yet)
// all four chart bands resolve to `null` and the chart skips those
// dashed lines, leaving just the data line on the grid.

// Build the chart descriptors for every sensor reported by the device.
// Each chart pulls its series straight from the per-sensor
// `datalogsBySensor` map; when the API has resolved with no rows the chart
// renders the "No data found" empty-state — no mock fallback.
//
// `alarmBandsBySensorId` maps the sensor's numeric id (`Sensor.id`,
// matching `alarmProfile[].sensorId`) to the bands returned by
// `bandsFromAlarmProfile`. Sensors with no matching profile render
// without threshold lines; there is no local fallback by design.
const buildSensorCharts = (sensors, datalogsBySensor = {}, alarmBandsBySensorId = {}) => {
  if (!Array.isArray(sensors) || sensors.length === 0) {
    return []
  }
  // Stable display order: Temperature → Humidity → Dewpoint → others.
  // Hard-coded here so the priority list is explicit (substring fuzzy
  // matches against `SENSOR_PALETTE` keys are non deterministic when the
  // API returns names like "Rel. Humidity" vs "Humidity"). Unknown
  // sensors fall to the end in their original array order.
  const priority = (name) => {
    const n = (name || '').toLowerCase()
    if (n.includes('temperature')) return 0
    if (n.includes('humidity')) return 1
    if (n.includes('dewpoint') || n.includes('dew point')) return 2
    return 3
  }
  const sortedSensors = [...sensors].sort((a, b) => priority(a.name) - priority(b.name))
  return sortedSensors.map((sensor, idx) => {
    const nameKey = findKey(sensor.name, SENSOR_PALETTE)
    const palette = nameKey
      ? SENSOR_PALETTE[nameKey] || {
          stroke: CHART_LINE_COLOR,
          warn: '#ffa65c',
          crit: '#ff3d57',
        }
      : { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' }
    const slug = sensor.slug ?? sensor.name
    const liveRows = datalogsBySensor[slug]
    const liveSeries = liveRows ? mapDatalogsToSeries(liveRows) : []
    // API bands are indexed by the sensor catalogue's id (`sensorId`),
    // `id`, and `uuid`. The device-detail payload surfaces the same
    // sensor under different field names depending on the API contract,
    // so we try each candidate against each indexed key. When none
    // match, all four band values stay `null` and the chart skips the
    // dashed lines — there's no local fallback by design.
    const sensorKeys = [sensor.id, sensor.sensorId, sensor.uuid].filter(
      (k) => k !== null && k !== undefined
    )
    let apiBands = []
    for (const k of sensorKeys) {
      if (alarmBandsBySensorId?.[k]) {
        apiBands = alarmBandsBySensorId[k]
        break
      }
    }
    // Multiple alarmProfiles for one sensor → multiple band groups, each
    // rendered as its own 4-line set (warn upper/lower + crit upper/lower).
    // When the API hasn't returned anything for this sensor the chart
    // skips all threshold lines.
    const bands = Array.isArray(apiBands) ? apiBands : []
    return {
      key: sensor.uuid ?? `${sensor.name ?? 'sensor'}-${idx}`,
      label: sensor.unit ? `${sensor.name} (${sensor.unit})` : sensor.name,
      series: liveSeries,
      palette,
      unit: sensor.unit,
      bands,
    }
  })
}

// Format a Y-axis tick value as a plain integer. Decimals are dropped
// so labels stay short and easy to scan, matching the screenshot's
// "56  %RH"-style rendering. NaN / undefined fall through to `--` so
// an empty series still renders readable ticks instead of blank squares.
const formatTickValue = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return Math.round(n).toString()
}

// Generic Y-axis mapper. Scales the series' [min, max] onto the SVG height
// so different sensors (°C, %, etc.) all fill the chart area regardless of
// their absolute range. Every threshold from every configured alarm
// profile folds into the range so all dashed lines stay visible even
// when the data sits well outside the bands (e.g. Humidity data 20–50 %
// with a 70 % crit-upper line from a sibling profile).
const buildScaler = (series, bands = []) => {
  const values = series.map((d) => d.v)
  const extras = []
  for (const band of bands) {
    for (const v of [band?.warnUpper, band?.warnLower, band?.critUpper, band?.critLower]) {
      if (v !== null && v !== undefined && Number.isFinite(Number(v))) extras.push(Number(v))
    }
  }
  const all = [...values, ...extras]
  const min = all.length ? Math.min(...all) : 0
  const max = all.length ? Math.max(...all) : 1
  const span = max - min || 1
  const toY = (v) => CHART_H - ((v - min) / span) * (CHART_H - 20) - 10
  // Inverse for the Y-axis labels — given a pixel Y in the chart area,
  // return the data value that lands there. Empty series falls back to
  // `min` so labels still render predictably (avoids `NaN` text).
  const toValue = (y) => {
    if (!all.length) return min
    return min + ((CHART_H - y - 10) / (CHART_H - 20)) * span
  }
  return { toY, toValue, min, max }
}

// Per-sensor SVG chart. Mirrors the original temperature chart's grid +
// gradient area + tolerance lines, but parametised by stroke colour,
// gradient id, and unit so each sensor renders with its own identity.
const SensorChart = ({ series, palette, unit, bands = [] }) => {
  // Active hover point + its X position in SVG coordinates. `null` means
  // no hover. Tracking only `index` (not the whole point) keeps the
  // tooltip in sync with the latest series if the parent swaps data.
  const [hoverIdx, setHoverIdx] = useState(null)
  const [hoverX, setHoverX] = useState(null)
  // Empty series — render a placeholder so the layout doesn't collapse on
  // an axis with no path. The grid still draws so the slot reads as
  // "chart-shaped, just no data".
  if (!Array.isArray(series) || series.length === 0) {
    return (
      <div className="sensor-chart sensor-chart--empty">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none">
          {GRID_Y.map((y) => (
            <line key={y} x1="0" y1={y} x2={CHART_W} y2={y} className="sensor-chart__grid" />
          ))}
        </svg>
        <div className="sensor-chart__empty-overlay">No data found</div>
      </div>
    )
  }
  const { toY, toValue } = buildScaler(series, bands)
  // Flatten every configured band (one set per alarmProfile) into a list
  // of dashed lines. React keys include the band index so two profiles
  // with identical numeric bounds still render as separate lines.
  const thresholdLines = []
  bands.forEach((band, bandIdx) => {
    const entries = [
      { value: band?.warnUpper, color: palette.warn, kind: 'warn-upper', dash: '6 4' },
      { value: band?.warnLower, color: palette.warn, kind: 'warn-lower', dash: '6 4' },
      { value: band?.critUpper, color: palette.crit, kind: 'crit-upper', dash: '4 2' },
      { value: band?.critLower, color: palette.crit, kind: 'crit-lower', dash: '4 2' },
    ]
    for (const e of entries) {
      if (e.value === null || e.value === undefined) continue
      thresholdLines.push({ ...e, key: `b${bandIdx}-${e.kind}-${e.value}` })
    }
  })
  const line = `M ${series
    .map((d, i) => `${(i * CHART_W) / (series.length - 1)},${toY(d.v)}`)
    .join(' L ')}`

  // X position of every data point in SVG coordinates. Used by both the
  // hover hit-area circles and the crosshair/active-marker overlay so
  // they stay aligned with the line.
  const xs = series.map((_, i) => (i * CHART_W) / (series.length - 1))

  // Snap a cursor X (in SVG coords) to the nearest data-point index.
  // Equal split means the hover always lands on a real reading instead
  // of interpolating between samples.
  const snapToIndex = (x) => {
    if (!Number.isFinite(x) || xs.length === 0) return null
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < xs.length; i += 1) {
      const d = Math.abs(xs[i] - x)
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    return bestIdx
  }

  const onSvgMove = (e) => {
    const svg = e.currentTarget
    // Convert client coords → SVG viewBox coords so the snap math works
    // regardless of the rendered chart size (responsive width).
    const rect = svg.getBoundingClientRect()
    const xRatio = CHART_W / rect.width
    const x = (e.clientX - rect.left) * xRatio
    const idx = snapToIndex(x)
    setHoverIdx(idx)
    setHoverX(idx === null ? null : xs[idx])
  }
  const onSvgLeave = () => {
    setHoverIdx(null)
    setHoverX(null)
  }

  // Tooltip content for the currently hovered point. `time` already comes
  // pre-formatted (HH:mm) from `mapDatalogsToSeries`. When `hoverIdx`
  // falls outside the series range (race during data swap), suppress.
  const hoverPoint = hoverIdx !== null && series[hoverIdx] ? series[hoverIdx] : null
  const hoverY = hoverPoint ? toY(hoverPoint.v) : null
  // Tooltip sticks to the top-left corner of the chart so it never
  // collides with the line or threshold bands and never gets clipped by
  // the card edge, regardless of where the cursor is. A small inset
  // (`8px`) keeps it visually separated from the top-left corner.
  const tooltipLeftPx = '8px'
  const tooltipTopPx = '28px'
  void hoverX
  void hoverY

  return (
    <div className="sensor-chart">
      {/* Y-axis labels live in a dedicated column OUTSIDE the SVG so the
          numbers never overlap the data line or the threshold bands.
          Each label's `top` is computed from the gridline Y inside the
          SVG viewBox (0..CHART_H) mapped to a percentage so they track
          the rendered chart height regardless of container size. */}
      <div className="sensor-chart__y-axis">
        {GRID_Y.map((y) => (
          <span
            key={`yt-${y}`}
            className="sensor-chart__y-label"
            style={{ top: `${(y / CHART_H) * 100}%` }}
          >
            {formatTickValue(toValue(y))}
          </span>
        ))}
      </div>
      <svg
        className="sensor-chart__svg"
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        preserveAspectRatio="none"
        onMouseMove={onSvgMove}
        onMouseLeave={onSvgLeave}
      >
        {GRID_Y.map((y) => (
          <line key={y} x1="0" y1={y} x2={CHART_W} y2={y} className="sensor-chart__grid" />
        ))}
        <path d={line} fill="none" stroke={palette.stroke} strokeWidth="2" />
        {thresholdLines.map((t) => (
          <line
            key={`overlay-${t.key}-${t.value}`}
            x1="0"
            y1={toY(t.value)}
            x2={CHART_W}
            y2={toY(t.value)}
            stroke={t.color}
            strokeDasharray={t.dash}
            strokeWidth="1.6"
            strokeOpacity="0.95"
          />
        ))}
        {/* Invisible hit-areas — capture mouse events for every data
            point so the tooltip snaps to the nearest sample instead of
            tracking raw pixel position. Larger radius (12) than the
            visible marker (4) so adjacent points are easy to land on. */}
        {xs.map((x, i) => (
          <circle
            key={`hit-${i}`}
            cx={x}
            cy={toY(series[i].v)}
            r={12}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
          />
        ))}
        {/* Active crosshair: vertical guide + ringed marker on the line
            at the hovered point. Drawn last so it sits over the area fill. */}
        {hoverX !== null && hoverY !== null && (
          <g pointerEvents="none">
            <line
              x1={hoverX}
              y1={0}
              x2={hoverX}
              y2={CHART_H}
              stroke={palette.stroke}
              strokeOpacity="0.45"
              strokeDasharray="3 3"
              strokeWidth="1"
            />
            <circle
              cx={hoverX}
              cy={hoverY}
              r={4.5}
              fill={palette.stroke}
              stroke="#fff"
              strokeWidth="1.5"
            />
          </g>
        )}
      </svg>
      {/* Tooltip rendered as an absolutely-positioned overlay so it can
          escape the SVG viewBox and use real CSS for typography. Pointer
          events disabled so the SVG underneath still receives mousemove
          and the cursor stays as `crosshair`. */}
      {hoverPoint && tooltipLeftPx !== null && tooltipTopPx !== null && (
        <div
          className="sensor-chart__tooltip"
          style={{ left: tooltipLeftPx, top: tooltipTopPx }}
          role="tooltip"
          aria-live="polite"
        >
          <div className="sensor-chart__tooltip-value">
            <span style={{ color: palette.stroke }}>
              {Number.isFinite(hoverPoint.v) ? hoverPoint.v : '--'}
            </span>
            <span className="sensor-chart__tooltip-unit">{unit || ''}</span>
          </div>
          <div className="sensor-chart__tooltip-time">
            {hoverPoint.date ? `${hoverPoint.date} · ${hoverPoint.time}` : hoverPoint.time}
          </div>
        </div>
      )}
      <span className="sensor-chart__corner">{`24h · ${unit || ''}`}</span>
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────
export default function DeviceDetail() {
  // Re-render this page when the admin changes the Global Settings
  // timezone, so every timestamp below re-formats immediately.
  useTimezone()
  const { uuid } = useParams()
  const navigate = useNavigate()

  // Fetch the device details from /devices/{uuid}. The response body is
  // wrapped in `{ success, message, data }`; the device object lives at
  // `data`. Anything not in the API response falls back to the existing
  // mock data in the JSX so the page layout is preserved.
  const { data: deviceResponse } = useQuery({
    queryKey: ['device', uuid],
    queryFn: () => getDeviceByUuid(uuid),
    enabled: Boolean(uuid),
    refetchOnMount: 'always',
  })
  const device = deviceResponse?.data?.data ?? deviceResponse?.data ?? null

  const validityDays = diffFromNow(device?.calibrationExpiryDate, 'days')
  // Mirror the calibration validity badge for the license card. `null`
  // means no expiry is set on the device; the badge falls through to a
  // dashed `--` placeholder so the row never collapses the layout.
  const licenseValidityDays = diffFromNow(device?.licenseExpiry, 'days')

  // 24h window for chart fetches. Anchored at mount time so all per-sensor
  // queries share the same `startDate`/`endDate` window. Refresh on remount
  // via the `enabled` flag + `staleTime` (see query config below).
  const window = useMemo(() => {
    const endDate = new Date()
    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000)
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
  }, [])

  // Parallel fetch: one /device-datalogs/filter call per reported sensor.
  // `useQueries` fires them concurrently and preserves per-sensor loading
  // state, so a single slow sensor doesn't block the rest of the grid.
  const serialNumber = device?.serialNumber
  const datalogQueries = useQueries({
    queries: (device?.sensors ?? []).map((sensor) => ({
      queryKey: [
        'device-datalogs',
        serialNumber,
        sensor.slug ?? sensor.name,
        window.startDate,
        window.endDate,
      ],
      queryFn: () =>
        filterDeviceDatalogsApi({
          serialNumber,
          sensorSlug: sensor.slug ?? sensor.name,
          startDate: window.startDate,
          endDate: window.endDate,
        }),
      enabled: Boolean(serialNumber) && Boolean(sensor.slug ?? sensor.name),
      staleTime: 60_000,
    })),
  })
  const datalogsBySensor = (device?.sensors ?? []).reduce((acc, sensor, i) => {
    const slug = sensor.slug ?? sensor.name
    const response = datalogQueries[i]?.data
    const rows = response?.data?.data.datalog ?? []
    acc[slug] = Array.isArray(rows) ? rows : []
    return acc
  }, {})

  // Parallel fetch: one /alarm-profiles/{uuid} call per template the
  // device reports. Templates are surfaced on the device payload under
  // `alarmProfileTemplates`, each carrying a `uuid` (and metadata). One
  // slow profile doesn't block the others via `useQueries`.
  const alarmTemplates = Array.isArray(device?.alarmProfileTemplates)
    ? device.alarmProfileTemplates
    : []
  const alarmProfileQueries = useQueries({
    queries: alarmTemplates.map((tpl) => ({
      queryKey: ['alarm-profile', tpl?.uuid],
      queryFn: () => getAlarmProfileByUuid(tpl?.uuid),
      enabled: Boolean(tpl?.uuid),
      staleTime: 60_000,
    })),
  })

  // Group the resolved profiles by template so a single template with both
  // WARNING and CRITICAL bands renders as one card containing both
  // severity sub-rows. Each entry carries the resolved template payload
  // + a map keyed by severity for fast lookup in the JSX.
  const alarmGroups = useMemo(() => {
    return alarmTemplates
      .map((tpl, idx) => {
        const data = alarmProfileQueries[idx]?.data?.data?.data
        if (!data) return null
        const bands = Array.isArray(data.profiles) ? data.profiles : []
        const bySeverity = {}
        bands.forEach((band) => {
          const sev = (band?.severity || '').toUpperCase()
          if (!sev) return
          bySeverity[sev] = band
        })
        return {
          uuid: tpl?.uuid,
          profileName: data.name || tpl?.name || 'Alarm profile',
          status: data.status,
          warn: bySeverity.WARNING,
          crit: bySeverity.CRITICAL,
        }
      })
      .filter(Boolean)
  }, [alarmTemplates, alarmProfileQueries])

  const alarmBandsBySensorId = useMemo(() => {
    const source = datalogQueries.find(
      (q) =>
        Array.isArray(q?.data?.data?.data?.alarmProfile) && q.data.data.data.alarmProfile.length > 0
    )
    const profiles = source?.data?.data?.data?.alarmProfile
    if (!Array.isArray(profiles) || profiles.length === 0) return {}
    return profiles.reduce((acc, profile) => {
      const bands = bandsFromAlarmProfile(profile?.profiles)
      // Each alarmProfile yields its own set of threshold lines so the
      // chart shows every configured band instead of collapsing them
      // into a single envelope. Multiple profiles for the same sensor
      // (e.g. separate High Temp / Low Temp profiles) each contribute
      // their own 4-line group.
      const pushInto = (key) => {
        if (key == null) return
        if (!acc[key]) acc[key] = []
        acc[key].push({ ...bands, name: profile?.name || '' })
      }
      pushInto(profile?.sensorId)
      pushInto(profile?.uuid)
      return acc
    }, {})
  }, [datalogQueries])

  // Build the Parameters cell from `device.sensors` when the API returns
  // any — each sensor renders as a StatusBadge chip showing its name
  // (and unit, when available). Falls back to the original mock badges
  // when the array is absent or empty so the layout never collapses.
  const apiSensorChips = (device?.sensors ?? [])
    .filter((s) => s && (s.name || s.unit))
    .map((sensor) => (
      <StatusBadge key={sensor.uuid ?? sensor.name} color="default">
        {sensor.unit ? `${sensor.name} ${sensor.unit}` : sensor.name}
      </StatusBadge>
    ))
  const parametersCell =
    apiSensorChips.length > 0 ? (
      <div className="flex gap-1 flex-wrap">{apiSensorChips}</div>
    ) : (
      'no sensors found'
    )

  // One chart per reported sensor. Match by name (case-insensitive) against
  // the mock series, fall back to a deterministic per-sensor series so even
  // unmapped sensors still render. Falls back to the original temperature
  // chart when `device.sensors` is empty so the layout never collapses.
  const sensorCharts = buildSensorCharts(
    device?.sensors ?? [],
    datalogsBySensor,
    alarmBandsBySensorId
  )

  // Calibration rows — only the "Expire date" cell is data-driven (from
  // device?.calibrationExpiryDate). The other three cells keep their
  // original static values because the API doesn't expose equivalents yet.
  // The Validity cell computes days-remaining against today; renders a
  // dashed `--` placeholder when no expiry date is set.
  const validityCell =
    validityDays === null ? (
      '--'
    ) : validityDays >= 0 ? (
      <StatusBadge color="green" dot>
        {`Valid · ${validityDays} days`}
      </StatusBadge>
    ) : (
      <StatusBadge color="red" dot>
        {`Expired ${Math.abs(validityDays)} days ago`}
      </StatusBadge>
    )
  const calibrationRows = [
    { label: 'Type', value: 'Accredited (ISO 17025)' },
    { label: 'Calibration date', value: '03 Dec 2025' },
    { label: 'Expire date', value: formatDateOnly(device?.calibrationExpiryDate) || '--' },
    { label: 'Validity', value: validityCell },
  ]

  // License rows mirror the calibration pattern: a static `Validity` cell
  // driven by the days-remaining computation against today, and two
  // data-driven cells (`Note` / `Expiry`) sourced from the device
  // payload. When `licenseExpiry` is unset `diffFromNow` returns `null`
  // and the badge falls through to `--` so the layout stays intact.
  const licenseValidityCell =
    licenseValidityDays === null ? (
      '--'
    ) : licenseValidityDays >= 0 ? (
      <StatusBadge color="green" dot>
        {`Valid · ${licenseValidityDays} days`}
      </StatusBadge>
    ) : (
      <StatusBadge color="red" dot>
        {`Expired ${Math.abs(licenseValidityDays)} days ago`}
      </StatusBadge>
    )
  const licenseRows = [
    { label: 'Note', value: device?.licenseNote ?? '--' },
    { label: 'Expiry', value: formatDateOnly(device?.licenseExpiry) || '--' },
    { label: 'Validity', value: licenseValidityCell },
  ]

  // Build the Identification rows from the derived values. Every row from
  // the original IDENTIFICATION_ROWS array is preserved — only the
  // `value`s that have API equivalents swap in the API data; everything
  // else keeps its prior mock fallback.
  const identificationRows = [
    { label: 'Model number', value: device?.modelNumber ?? '--' },
    { label: 'Sensor name', value: device?.name ?? device?.deviceName ?? 'TH-14 Cold A' },
    { label: 'Device serial', value: device?.serialNumber ?? 'KL-22014-08' },
    { label: 'Device type', value: device?.type ?? device?.deviceType ?? 'Active' },
    { label: 'Multi-use device', value: device?.multiUse ?? device?.multiUseDevice ?? 'Yes' },
    { label: 'Reporting interval', value: device?.reportingInterval ?? '11 minutes' },
    { label: 'Start delay', value: device?.startDelay ?? '5 minutes' },
    // File upload has no API field yet — keep the mock value.
    { label: 'File upload', value: 'Upload calibration certificate' },
    { label: 'Expire date', value: formatDateOnly(device?.calibrationExpiryDate) || '--' },
    { label: 'Brand / vendor', value: device?.manufacturer?.name ?? '--' },
    { label: 'AssetTag serial', value: device?.assetTag?.name ?? '--' },
    // Parameters now comes from `device.sensors` (mocked when absent).
    { label: 'Parameters', value: parametersCell },
  ]

  // Derive current-reading cards from `device.lastLogData`. Computed
  // once per render so the JSX block stays a pure map over the result.
  const currentReadings = buildCurrentReadings(device?.lastLogData)

  return (
    <div className="sensor-page">
      {/* Top Bar */}
      <div className="sensor-topbar">
        <div>
          {/* Sits above the breadcrumb so the user can return to the
              device listing page without using the browser's back button.
              Uses navigate(-1) so the listing's scroll position, search
              input, and active filters are preserved. */}
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<ArrowLeft size={14} />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 1.5,
              ml: -1,
              textTransform: 'none',
              // Subtle primary-tinted rectangle on hover, darkening to a
              // deeper shade so the click target reads as a square button.
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.24)',
              },
            }}
          >
            Back
          </Button>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link underline="hover" color="inherit" href="/sensors">
              Sensors
            </Link>
            <p>Devices</p>
            <Typography variant="body2" color="text.primary">
              {device?.name ?? device?.deviceName ?? 'TH-14 Cold A'}
            </Typography>
          </Breadcrumbs>
          <div className="flex items-center gap-3 mb-1">
            <Typography variant="h5">
              {device?.name ?? device?.deviceName ?? 'TH-14 Cold A'}
            </Typography>
          </div>
          <Typography className="text-gray-400">
            {device?.manufacturer?.name ?? '--'} · serial {device?.serialNumber ?? 'KL-22014-08'} ·{' '}
            {device?.facility?.name ?? '--'} · {device?.location ?? '--'}
          </Typography>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outlined"
            startIcon={<TrendingUp size={14} />}
            onClick={() => {
              // Deep-link into the KPI (Monitoring & Analytics) page
              // with the current device's facility + cold room + device
              // pre-filled. KPI state expects the facility's uuid (so
              // the facility-detail query can hydrate the cold-room
              // hierarchy), the cold room's numeric PK (matching the
              // `flattenColdRooms` output and the /devices
              // `coldroomId` filter), and the device's numeric id
              // (the /devices `id` field). Period + sensor defaults
              // match the user's spec — day window, temperature
              // sensor. `location.state` is read by Kpi.jsx and feeds
              // the hook's `initialValues` + `autoApply` flags.
              navigate(ROUTES.KPI, {
                state: {
                  facilityId: device?.facility?.uuid ?? null,
                  coldRoomId: device?.coldRoom?.id ?? null,
                  // The KPI devices dropdown matches by numeric id
                  // (per the /devices list response shape) but we
                  // forward `device.uuid` as a fallback so the chip
                  // still surfaces if the byUuid endpoint ever drops
                  // the numeric alias.
                  deviceIds: [device?.id ?? device?.uuid].filter(Boolean),
                  typeId: 'temperature',
                  parameterId: 'temperature-c',
                  period: 'day',
                },
              })
            }}
          >
            Full trends
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="sensor-body">
        {/* LEFT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Current Readings — one card per `lastLogData` key. Each
              pill carries the label, value, unit and the device's shared
              `lastLogTime` so the user sees when the snapshot was taken. */}
          <Card className="px-4 py-3">
            <SectionHeader
              icon={Settings}
              label="Current Readings"
              action={
                device?.lastLogTime ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(device.lastLogTime)}
                  </Typography>
                ) : null
              }
            />
            {currentReadings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No current readings available
              </Typography>
            ) : (
              <div className="sensor-readings">
                {currentReadings.map((reading) => (
                  <div
                    key={reading.key}
                    className="sensor-reading-card"
                    title={`${reading.label}${reading.unit ? ` (${reading.unit})` : ''}`}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {reading.label}
                    </Typography>
                    <div className="sensor-reading-card__value">
                      <span className="sensor-reading-card__number">{reading.value ?? '--'}</span>
                      {reading.unit ? (
                        <span className="sensor-reading-card__unit">{reading.unit}</span>
                      ) : null}
                    </div>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(device?.lastLogTime) || '--'}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 24-Hour Trend — one chart per sensor reported by the device */}
          <Card className="p-5">
            <SectionHeader
              icon={TrendingUp}
              label={`24-Hour Trend${sensorCharts.length > 1 ? 's' : ''}`}
              action={<span className="section-header__hint">Tolerance band shown</span>}
            />
            <div className="sensor-legend">
              <LegendItem color="#ff3d57" label="Critical" />
              <LegendItem color="#ffa65c" label="Warning" />
            </div>
            <div className="flex flex-col gap-4">
              {sensorCharts.map((chart) => (
                <div key={chart.key}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1, color: 'var(--text-primary)' }}
                  >
                    {chart.label}
                  </Typography>
                  <SensorChart
                    series={chart.series}
                    palette={chart.palette}
                    unit={chart.unit}
                    bands={chart.bands}
                  />
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <SectionHeader
              icon={Bell}
              label={
                <>
                  Alarm Profiles{' '}
                  <span className="section-header__count">
                    · {alarmGroups.length} of {alarmTemplates.length || 0}
                  </span>{' '}
                </>
              }
            />
            <div className="sensor-alarm-list">
              {alarmGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No alarm profiles attached
                </Typography>
              ) : (
                alarmGroups.map((group) => {
                  // Render helper: turns a single severity band into the
                  // (label+icon, range string) tuple used by both WARNING
                  // and CRITICAL rows inside the group card. Icon sits
                  // immediately beside the severity label so the level
                  // reads as a single unit ("WARNING" next to its bell).
                  const bandRow = (band, sev) => {
                    if (!band) return null
                    const min = band.minValue
                    const max = band.maxValue
                    const minNum = Number(min)
                    const maxNum = Number(max)
                    const minFinite = Number.isFinite(minNum)
                    const maxFinite = Number.isFinite(maxNum)
                    const rangeText =
                      minFinite && maxFinite
                        ? minNum === maxNum
                          ? `= ${min}`
                          : `${min} – ${max}`
                        : maxFinite
                          ? `> ${max}`
                          : minFinite
                            ? `< ${min}`
                            : ''
                    const delayText = band.alarmDelay ? `delay ${band.alarmDelay} min` : ''
                    const isCrit = sev === 'CRITICAL'
                    const iconClass = isCrit ? 'sensor-alarm--critical' : 'sensor-alarm--warning'
                    const Icon = Bell
                    return (
                      <div
                        key={`${group.uuid}-${sev}`}
                        className={`sensor-alarm__band sensor-alarm__band--${isCrit ? 'critical' : 'warning'}`}
                      >
                        <span className={`sensor-alarm__icon ${iconClass}`}>
                          <Icon size={16} strokeWidth={2.5} />
                        </span>
                        <span className="sensor-alarm__band-label">{sev}</span>
                        <span className="sensor-alarm__band-text">
                          {rangeText}
                          {rangeText && delayText ? ' · ' : ''}
                          {delayText}
                        </span>
                      </div>
                    )
                  }
                  const isActive = (group.status || '').toUpperCase() === 'ACTIVE'
                  return (
                    <div key={group.uuid} className="sensor-alarm sensor-alarm--group">
                      <div className="sensor-alarm__left">
                        <span
                          className={`sensor-alarm__icon ${
                            isActive ? 'sensor-alarm--active' : 'sensor-alarm--critical'
                          }`}
                        >
                          <Bell size={18} />
                        </span>
                        <div>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {group.profileName}
                          </Typography>
                          <div className="sensor-alarm__bands">
                            {bandRow(group.warn, 'WARNING')}
                            {bandRow(group.crit, 'CRITICAL')}
                          </div>
                        </div>
                      </div>
                      <div className="sensor-alarm__right">
                        <StatusBadge color={isActive ? 'green' : 'red'} dot>
                          {group.status || 'Inactive'}
                        </StatusBadge>
                        <Button
                          size="small"
                          variant="outlined"
                          className="sensor-btn-tiny"
                          onClick={() => navigate(`/alarms/details/${group.uuid}`)}
                        >
                          Manage
                        </Button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </Card>

          {/* AssetTag Swap History */}
          <Card className="p-5">
            <SectionHeader
              icon={Tag}
              label={`Asset Tag Swap History — ${device?.assetTag?.name ?? '--'}`}
            />
            <AssetTagHistoryTable uuid={device?.assetTag?.uuid} />
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Identification */}
          <Card className="p-5">
            <SectionHeader icon={IdCard} label="Identification" />
            {identificationRows.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
          </Card>

          {/* Calibration */}
          <Card className="p-5">
            <SectionHeader icon={Settings} label="Calibration" />
            {calibrationRows.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download size={14} />}
              sx={{ marginTop: '12px' }}
              // Render as an anchor so the browser handles the file
              // download natively (presigned URLs, signed S3 links, etc.).
              // `target="_blank"` opens in a new tab as a fallback when the
              // response's `Content-Disposition` doesn't trigger a direct
              // download. The button is disabled when the device has no
              // certificate attached — clicking then is a no-op rather than
              // navigating to `undefined`.
              component="a"
              href={device?.calibrationCertificateUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              download
              disabled={!device?.calibrationCertificateUrl}
            >
              Download certificate
            </Button>
          </Card>

          {/* License — surfaces the device's license note + expiry from
              the API. Mirrors the Calibration card's three-row layout:
              note / expiry / validity badge. No file to download for a
              license, so the card stops at the validity row. */}
          <Card className="p-5">
            <SectionHeader icon={Tag} label="License" />
            {licenseRows.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
          </Card>

          {/* Device Health */}
          <Card className="p-5">
            <SectionHeader icon={Activity} label="Device Health" />
            <div className="flex flex-col">
              {buildHealthRows(device).map((row) => (
                <InfoRow key={row.label} {...row} />
              ))}
            </div>
          </Card>

          {/* Location */}
        </div>
      </div>
    </div>
  )
}
